"use client";

import { createContext, useContext, useState, useEffect, startTransition, type ReactNode } from "react";
import type { CityData } from "@/lib/cities-data";
import { CITY_MAP } from "@/lib/cities-data";

const DEFAULT_SLUG = "new-delhi";
const LS_KEY = "ish_city";

interface PanchangCityCtx {
  city: CityData;
  setCity: (city: CityData) => void;
}

const Ctx = createContext<PanchangCityCtx>({
  city: CITY_MAP[DEFAULT_SLUG],
  setCity: () => {},
});

export function PanchangCityProvider({ children }: { children: ReactNode }) {
  const [city, setCity] = useState<CityData>(CITY_MAP[DEFAULT_SLUG]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw) as Partial<CityData>;
      if (stored.slug && typeof stored.latitude === "number" && typeof stored.longitude === "number") {
        // Prefer curated CityData (richer metadata); fall back to stored data for non-curated cities.
        const c = CITY_MAP[stored.slug] ?? (stored as CityData);
        startTransition(() => setCity(c));
      }
    } catch {
      // localStorage unavailable or stale
    }
  }, []);

  const handleSetCity = (incoming: CityData) => {
    setCity(incoming);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        slug: incoming.slug,
        name: incoming.name,
        state: incoming.state,
        latitude: incoming.latitude,
        longitude: incoming.longitude,
        timezone: incoming.timezone,
      }));
    } catch {}
  };

  return <Ctx.Provider value={{ city, setCity: handleSetCity }}>{children}</Ctx.Provider>;
}

export function usePanchangCity(): PanchangCityCtx {
  return useContext(Ctx);
}
